import { useState, useCallback, useEffect, useRef } from "react";
import firebase from "../config/firebase";
import { toast } from "react-toastify";

/**
 * Custom hook for managing Firebase email verification
 * Provides utilities for sending verification emails and checking verification status
 */
const useEmailVerification = ({ onVerified, pollInterval = 5000 } = {}) => {
  const [isSending, setIsSending] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const onVerifiedRef = useRef(onVerified);

  // Keep the callback ref current without restarting the polling effect
  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  // Automatically poll Firebase for email verification status
  useEffect(() => {
    let active = true;

    const poll = async () => {
      const user = firebase.auth().currentUser;
      if (!user || user.emailVerified) return;

      try {
        await user.reload();
        if (active && firebase.auth().currentUser?.emailVerified) {
          onVerifiedRef.current?.();
        }
      } catch {
        // silently ignore transient network errors during polling
      }
    };

    const intervalId = setInterval(poll, pollInterval);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [pollInterval]);

  /**
   * Send verification email with spam protection (60-second cooldown)
   * @returns {Promise<boolean>} Success status
   */
  const sendVerificationEmail = useCallback(async () => {
    if (cooldownRemaining > 0) {
      toast.warning(
        `Please wait ${cooldownRemaining} seconds before resending`
      );
      return false;
    }

    const user = firebase.auth().currentUser;

    if (!user) {
      toast.error("No user is currently logged in");
      return false;
    }

    if (user.emailVerified) {
      toast.info("Your email is already verified");
      return false;
    }

    setIsSending(true);

    try {
      await user.sendEmailVerification();
      toast.success("Verification email sent! Please check your inbox");

      // Start 60-second cooldown timer
      setCooldownRemaining(60);
      const interval = setInterval(() => {
        setCooldownRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return true;
    } catch (error) {
      console.error("Error sending verification email:", error);

      // Handle specific Firebase errors
      if (error.code === "auth/too-many-requests") {
        toast.error("Too many requests. Please try again later");
      } else if (error.code === "auth/network-request-failed") {
        toast.error("Network error. Please check your connection");
      } else {
        toast.error(
          error.message || "Failed to send verification email. Please try again"
        );
      }

      return false;
    } finally {
      setIsSending(false);
    }
  }, [cooldownRemaining]);

  /**
   * Check if current user's email is verified
   * Reloads user data from Firebase to get latest verification status
   * @returns {Promise<boolean>} Verification status
   */
  const checkEmailVerification = useCallback(async () => {
    const user = firebase.auth().currentUser;

    if (!user) {
      toast.error("No user is currently logged in");
      return false;
    }

    try {
      // Reload user to get latest emailVerified status from server
      await user.reload();
      return user.emailVerified;
    } catch (error) {
      console.error("Error checking email verification:", error);

      // Handle session expiration
      if (
        error.code === "auth/user-token-expired" ||
        error.code === "auth/user-disabled"
      ) {
        toast.error("Your session has expired. Please log in again");
        await firebase.auth().signOut();
        return false;
      }

      toast.error("Failed to check verification status. Please try again");
      return false;
    }
  }, []);

  /**
   * Get current user's email
   * @returns {string|null} User email or null
   */
  const getUserEmail = useCallback(() => {
    const user = firebase.auth().currentUser;
    return user ? user.email : null;
  }, []);

  return {
    sendVerificationEmail,
    checkEmailVerification,
    getUserEmail,
    isSending,
    cooldownRemaining,
  };
};

export default useEmailVerification;
